import { useRef, useState } from "react";
import { track } from "@/lib/analytics";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { useAuth } from "@/contexts/AuthContext";
import {
  Globe, Camera, ForkKnife, Sparkle, ArrowRight, CircleNotch, CheckCircle,
} from "@phosphor-icons/react";

/**
 * Anonymous "try me" hero flow.
 *
 * Two tabs:
 *   1. Paste a URL   → POST /import/scan-url  (schema.org scrape)
 *   2. Scan a photo  → POST /import/scan-image (LlamaParse + GPT-4o Vision)
 *
 * Both routes are auth-optional and uncapped — visitors can try as often as they like.
 */
export default function TryImport() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const submitUrl = async (override) => {
    const trimmed = (typeof override === "string" ? override : url).trim();
    if (!trimmed) { toast.error("Paste a recipe URL"); return; }
    if (!trimmed.startsWith("http")) { toast.error("URL must start with http:// or https://"); return; }
    setBusy(true);
    try {
      const r = await api.post("/import/scan-url", { url: trimmed });
      track("import_recipe", { source: "url" });
      setPreviews((p) => [{ job_id: r.data.job_id, recipe: r.data.recipe, source: "url" }, ...p]);
      setUrl("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't find a recipe on that page");
    } finally {
      setBusy(false);
    }
  };

  const submitScan = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/import/scan-image", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      track("scan_recipe", { pages: 1, scan_type: "try_import" });
      setPreviews((p) => [{ job_id: r.data.job_id, recipe: r.data.recipe, source: "scan" }, ...p]);
      toast.success("Scanned!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed — try a clearer photo");
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const saveFirst = async (job_id) => {
    if (!user) {
      toast.info("Sign in and we'll save this for you automatically.");
      const returnTo = `/import?job=${job_id}&auto_commit=1`;
      nav(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/import/${job_id}/commit`, { selected: [0] });
      toast.success("Saved!");
      nav(`/cb/${r.data.cookbook_id}?just_imported=1`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <PageMeta
        title="Try RecipeFiler — scan or paste a recipe URL, free, no sign-up"
        description="Paste any recipe URL or upload a photo of a handwritten recipe — see it beautifully reformatted in seconds. No sign-up needed to try. 3 free previews."
        canonical="https://recipefiler.com/try"
      />
      <BreadcrumbJsonLd items={[
        { name: "Home", url: "https://recipefiler.com/" },
        { name: "Try it free", url: "https://recipefiler.com/try" },
      ]} />
      {/* Product schema — surfaces the free-preview offer in Google */}
      <script type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "RecipeFiler — recipe importer",
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "GBP" },
          "description": "Free anonymous preview of any recipe URL or scanned photo — no sign-up needed."
        }) }} />

      <div className="inline-flex items-center gap-2 rounded-full bg-rf-amber/10 text-rf-orange px-3 py-1 text-xs font-semibold uppercase tracking-wider">
        <Sparkle weight="fill" size={14} /> Free preview · No sign-up
      </div>
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight" data-testid="try-h1">
        Paste a URL or snap a photo. See it beautifully reformatted in 5 seconds.
      </h1>
      <p className="mt-4 text-lg text-slate-600 max-w-3xl">
        Try RecipeFiler with any recipe you love. We'll strip out the life story, rehost the photo,
        and drop it into a clean editable card. Save it forever when you're ready.
      </p>

      {/* Tab switcher */}
      <div className="mt-8 flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTab("url")}
          data-testid="try-tab-url"
          className={"px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 " +
            (tab === "url" ? "border-rf-orange text-rf-orange" : "border-transparent text-slate-500 hover:text-slate-800")}>
          <Globe size={18} /> Paste a URL
        </button>
        <button
          onClick={() => setTab("scan")}
          data-testid="try-tab-scan"
          className={"px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 " +
            (tab === "scan" ? "border-rf-orange text-rf-orange" : "border-transparent text-slate-500 hover:text-slate-800")}>
          <Camera size={18} /> Scan a photo
        </button>
      </div>

      {tab === "url" && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 shadow-sm">
          <Globe size={22} className="text-slate-400 shrink-0" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitUrl()}
            placeholder="https://cooking.nytimes.com/recipes/…"
            className="flex-1 outline-none text-slate-800 placeholder-slate-400"
            data-testid="try-url-input"
            disabled={busy}
          />
          <button
            onClick={submitUrl}
            disabled={busy}
            data-testid="try-submit"
            className="px-5 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-60 flex items-center gap-2 whitespace-nowrap shrink-0">
            {busy ? <><CircleNotch size={16} className="animate-spin" /> Fetching…</> : <>Try it <ArrowRight size={16} /></>}
          </button>
        </div>
      )}

      {tab === "scan" && (
        <div
          data-testid="try-scan-dropzone"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); submitScan(e.dataTransfer.files?.[0]); }}
          className="mt-5 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 p-10 text-center cursor-pointer transition-colors">
          <Camera size={40} className="mx-auto text-slate-400" />
          <p className="mt-3 font-semibold text-slate-700">Drop a recipe photo or PDF, or click to pick one</p>
          <p className="mt-1 text-sm text-slate-500">
            Handwritten recipe cards, magazine tears, screenshots — up to 10 MB.
            AI reads it and gives you a clean editable card.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="try-scan-input"
            onChange={(e) => submitScan(e.target.files?.[0])}
          />
          {busy && (
            <p className="mt-4 text-rf-orange text-sm flex items-center justify-center gap-2">
              <CircleNotch className="animate-spin" size={16} /> Scanning + structuring…
            </p>
          )}
        </div>
      )}

      <p className="mt-2 text-xs text-slate-500" data-testid="try-remaining">
        Try as many as you like — <Link to="/register" className="text-rf-orange underline">sign up free</Link> to save them.
      </p>

      {tab === "url" && <SamplePicks setUrl={setUrl} submit={submitUrl} />}

      {/* Preview cards */}
      <div className="mt-8 grid gap-6">
        {previews.map(({ job_id, recipe, source }) => (
          <PreviewCard
            key={job_id}
            recipe={recipe}
            source={source}
            onSave={() => saveFirst(job_id)}
            busy={busy}
            user={user}
          />
        ))}
      </div>

      {/* Unlocks on sign-up — reassure anon visitors that more import methods
          exist behind the free account so they don't think URL + Scan is the
          whole product. */}
      {!user && (
        <section className="mt-12 rounded-2xl border-2 border-rf-amber/40 bg-rf-cream p-6" data-testid="try-unlocks">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle weight="fill" size={18} className="text-rf-orange" />
            <h2 className="text-xl font-display font-semibold text-rf-ink">
              Sign up free to unlock every way in
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Here on <span className="font-semibold">/try</span> you get URL + Scan.
            Free accounts (10 recipes) unlock every other method too:
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
            {[
              "Import from Paprika (.paprikarecipes)",
              "Import from Mela (.melarecipes)",
              "Import from Recipe Keeper (.zip)",
              "Import from Cookmate (.mcb)",
              "Import from Copy Me That (CSV/JSON)",
              "Import from BigOven (.bxr / CSV)",
              "Import from Whisk / Samsung Food",
              "Import from AnyList (JSON)",
              "Import from Plan to Eat (CSV)",
              "Import from Zestio (JSON)",
              "Import from Notion (CSV/JSON)",
              "Import from Evernote (.enex)",
              "Import from MealMaster (.mmf)",
              "Import from Apple Notes / Google Keep / Obsidian",
              "Import from Cookpad / Yummly / HelloFresh / Substack URLs",
              "Import from browser bookmarks.html",
              "Paste up to 500 URLs at once",
              "Multi-page scans (up to 12 pages)",
              "Paste raw text (WhatsApp, email, note)",
              "Import from Instagram / TikTok posts",
              "Rescue from Clove, Osta, Recime, Pepperplate, Living Cookbook & MacGourmet",
              "Type it manually in the sandbox editor",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="try-unlocks-register"
              className="px-5 py-2.5 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 inline-flex items-center gap-2">
              Create a free account <ArrowRight size={16} />
            </Link>
            <Link
              to="/import"
              className="px-5 py-2.5 rounded-full border-2 border-rf-orange/40 text-rf-orange text-sm font-semibold hover:bg-rf-amber/10 inline-flex items-center gap-2">
              See the full Import hub
            </Link>
          </div>
        </section>
      )}

      {/* Upsell */}
      <section className="mt-14 rounded-2xl bg-slate-900 text-white p-8" data-testid="try-upsell">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div>
            <p className="text-rf-amber font-semibold text-sm uppercase tracking-wider">Ready for the whole shelf?</p>
            <h2 className="mt-2 text-3xl font-display font-semibold">
              Recime, Paprika, Clove, Recipe Keeper, Osta, Pepperplate, Samsung Food, Whisk + 22 more sources.
            </h2>
            <p className="mt-3 text-slate-300 text-sm">
              Bring every recipe from every app you've ever used into one, printable, forever-cookbook.
              Photos rehosted, categories preserved, no lock-in.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/import"
              data-testid="try-cta-full-import"
              className="px-5 py-3 rounded-full bg-rf-orange text-white font-semibold text-center hover:bg-rf-orange/90">
              Bring my whole collection →
            </Link>
            <Link to="/register" className="text-slate-300 hover:text-white text-sm text-center underline">
              Create a free account
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


function PreviewCard({ recipe, source, onSave, busy, user }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="try-preview-card">
      <div className="grid md:grid-cols-[240px,1fr]">
        <div className="bg-slate-100 aspect-video md:aspect-auto md:h-full overflow-hidden">
          {recipe.image_url ? (
            <img src={recipe.image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ForkKnife size={48} className="text-slate-300" />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
            {source === "scan" ? <><Camera size={12} /> Scanned</> : <><Globe size={12} /> From URL</>}
          </div>
          <h3 className="mt-1 text-2xl font-display font-semibold text-slate-800">{recipe.title || "Untitled recipe"}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {(recipe.ingredients || []).length} ingredients · {(recipe.instructions || []).length} steps
            {recipe.creator_name && <> · {recipe.creator_name}</>}
          </p>
          {recipe.description && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{recipe.description}</p>}
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-slate-700 mb-1">Ingredients</p>
              <ul className="text-slate-600 space-y-0.5 text-xs">
                {(recipe.ingredients || []).slice(0, 6).map((ing, i) => (
                  <li key={i}>
                    {ing.quantity ? `${ing.quantity} ` : ""}{ing.unit ? `${ing.unit} ` : ""}{ing.name}
                  </li>
                ))}
                {(recipe.ingredients || []).length > 6 && (
                  <li className="text-slate-400">+{recipe.ingredients.length - 6} more…</li>
                )}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">First steps</p>
              <ol className="text-slate-600 space-y-1 text-xs list-decimal list-inside">
                {(recipe.instructions || []).slice(0, 3).map((s, i) => (
                  <li key={i} className="line-clamp-2">{s}</li>
                ))}
              </ol>
            </div>
          </div>
          <button
            onClick={onSave}
            disabled={busy}
            data-testid="try-save-btn"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-60">
            <CheckCircle size={16} weight="fill" /> {user ? "Save to my cookbook" : "Sign in to save"}
          </button>
        </div>
      </div>
    </article>
  );
}


function SamplePicks({ setUrl, submit }) {
  const picks = [
    { label: "BBCGoodFood.com · Slow cooker bolognese", url: "https://www.bbcgoodfood.com/recipes/slow-cooker-spaghetti-bolognese" },
    { label: "JamieOliver.com · Chicken tikka masala", url: "https://www.jamieoliver.com/recipes/chicken/chicken-tikka-masala/" },
    { label: "Epicurious.com · Basil chicken stir-fry", url: "https://www.epicurious.com/recipes/food/views/ba-syn-basil-chicken-stir-fry" },
  ];
  return (
    <section className="mt-6" data-testid="try-samples">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Try a sample</p>
      <div className="flex flex-wrap gap-2">
        {picks.map((p) => (
          <button
            key={p.url}
            data-testid={`try-sample-${p.url.split("/")[2].replace("www.", "").split(".")[0]}`}
            onClick={() => { setUrl(p.url); submit(p.url); }}
            className="text-xs px-3 py-2.5 sm:py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold">
            {p.label}
          </button>
        ))}
      </div>
    </section>
  );
}
